// Client Admin — manage auth.users accounts linked to client portal logins.
//
// This is the admin counterpart to the /account portal a client logs into.
// It is NOT the same as /admin/clients (which manages the `clients` company
// table). Here we deal with the auth.users records, their linked clients,
// and password / disable lifecycle actions.
//
// Mirrors the Zonos / UnmatchedExports page layout: header → filter bar →
// card with table. Mutations open dedicated modals.

import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Search, X, Plus, Loader2, Edit2, KeyRound, Ban, CheckCircle2,
  ChevronUp, ChevronDown, ChevronsUpDown, MailCheck, UserPlus, AlertTriangle,
  ShieldOff, ShieldCheck, Trash2, Activity, Send,
} from 'lucide-react'
import {
  listClientUsers, createClientUser, updateClientUser,
  resetClientUserPassword, disableClientUser, enableClientUser,
  deleteClientUser, getAdminClients, getClientActivity,
  resendClientUserInvite, confirmClientUserEmail,
} from '../../lib/api'
import { ToastContainer, useToast } from '../../components/Toast'

// ─── Helpers ────────────────────────────────────────────────────────────────

function fmtDateTime(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-AU', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  } catch { return String(iso) }
}

function fmtDate(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('en-AU', {
      year: 'numeric', month: 'short', day: 'numeric',
    })
  } catch { return String(iso) }
}

// Short "dd MMM yy, HH:mm" — used in the Client Activity log where vertical
// density matters more than a full year.
function fmtDateTimeShort(iso) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    const date = d.toLocaleDateString('en-AU', {
      day: '2-digit', month: 'short', year: '2-digit',
    })
    const time = d.toLocaleTimeString('en-AU', {
      hour: '2-digit', minute: '2-digit', hour12: false,
    })
    return `${date}, ${time}`
  } catch { return String(iso) }
}

// yyyy-MM-dd for <input type="date" /> — used to seed the Client Activity
// date-range filter with a sensible default (last 30 days).
function isoDate(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function statusBadge(status) {
  const map = {
    active:   { cls: 'bg-emerald-100 text-emerald-700', label: 'Active' },
    invited:  { cls: 'bg-blue-100   text-blue-700',     label: 'Invited' },
    disabled: { cls: 'bg-red-100    text-red-700',      label: 'Disabled' },
  }
  const s = map[status] || { cls: 'bg-slate-100 text-slate-600', label: status }
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${s.cls}`}>{s.label}</span>
}

function roleBadge(clientRole) {
  if (clientRole === 'client_admin') {
    return <span className="text-xs px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">Client Admin</span>
  }
  return <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">Client User</span>
}

function SortHeader({ col, label, sort, onSort, align = 'left' }) {
  const isActive = sort.by === col
  const Icon = !isActive
    ? <ChevronsUpDown size={11} className="text-slate-300 ml-1 inline-block" />
    : sort.dir === 'asc'
      ? <ChevronUp size={11} className="text-blue-500 ml-1 inline-block" />
      : <ChevronDown size={11} className="text-blue-500 ml-1 inline-block" />
  return (
    <th
      onClick={() => onSort(col)}
      className={`px-3 py-3 font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap select-none cursor-pointer hover:text-slate-700 hover:bg-slate-100 transition-colors ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      {label}{Icon}
    </th>
  )
}

// ─── Main page ──────────────────────────────────────────────────────────────

export default function ClientUsersAdmin() {
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()

  const [search, setSearch]     = useState('')
  const [page, setPage]         = useState(1)
  const [perPage, setPerPage]   = useState(50)
  const [sort, setSort]         = useState({ by: 'created_at', dir: 'desc' })

  // Modal state. One of: null | { mode: 'create' } | { mode: 'edit', user } |
  // { mode: 'reset', user } | { mode: 'disable', user } | { mode: 'enable', user }
  const [modal, setModal] = useState(null)

  const params = useMemo(() => ({
    search:   search.trim() || undefined,
    page,
    per_page: perPage,
    sort_by:  sort.by,
    sort_dir: sort.dir,
  }), [search, page, perPage, sort])

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['client-users', params],
    queryFn:  () => listClientUsers(params),
    keepPreviousData: true,
  })

  // The clients list backs the multi-select in the create/edit modal.
  // Pulled once and cached — it's small (one row per client company).
  const { data: clientsList = [] } = useQuery({
    queryKey: ['client-list-for-link'],
    // The admin /clients endpoint returns enriched summaries; we just need id+name.
    queryFn:  async () => {
      const today = new Date()
      const period = { year: today.getFullYear(), month: today.getMonth() + 1 }
      const summaries = await getAdminClients(period)
      return (summaries || [])
        .map(c => ({ id: c.id, name: c.name }))
        .filter(c => c.id && c.name)
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    staleTime: 5 * 60 * 1000,
  })

  function toggleSort(col) {
    setSort(s => s.by === col ? { by: col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { by: col, dir: 'asc' })
    setPage(1)
  }

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['client-users'] })
  }

  // ── Mutations ──────────────────────────────────────────────────────────
  const createMut = useMutation({
    mutationFn: createClientUser,
    onSuccess: () => {
      addToast({ type: 'success', message: 'Client user created.' })
      invalidate()
      setModal(null)
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Create failed.' }),
  })

  const updateMut = useMutation({
    mutationFn: ({ userId, payload }) => updateClientUser(userId, payload),
    onSuccess: () => {
      addToast({ type: 'success', message: 'Saved.' })
      invalidate()
      setModal(null)
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Save failed.' }),
  })

  const resetMut = useMutation({
    mutationFn: ({ userId, payload }) => resetClientUserPassword(userId, payload),
    onSuccess: (_d, vars) => {
      addToast({
        type: 'success',
        message: vars.payload.mode === 'email'
          ? 'Password reset email sent.'
          : 'Password updated. User must change it on next sign-in.',
      })
      setModal(null)
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Reset failed.' }),
  })

  // Re-send the Supabase invite / set-password email. Shown for every user
  // regardless of status — invited accounts need a fresh magic link, active
  // accounts may have forgotten their password and want a new one. Confirmed
  // via window.confirm rather than a full modal to keep it a one-click action.
  const resendMut = useMutation({
    mutationFn: resendClientUserInvite,
    onSuccess: (data) => {
      // The server picks the email type from the account's state and tells
      // us which it sent. An active user gets a password reset, never a new
      // invite — a fresh invite would un-confirm them and lock them out.
      addToast({
        type: 'success',
        message: data?.mode === 'recovery'
          ? 'Password reset email sent.'
          : 'Invite email resent.',
      })
      // An invite re-stamps force_password_change, so an Invited user's row
      // can legitimately change. Refresh so the table matches.
      invalidate()
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || 'Resend failed.',
    }),
  })

  // Force-confirm the email. The counterpart to Resend: an account can end up
  // with a valid password but no confirmed email (older builds re-invited
  // active users, which nulls email_confirmed_at), and Supabase then rejects
  // sign-in with "Email not confirmed". This puts them straight back without
  // sending anything or touching their password.
  const confirmMut = useMutation({
    mutationFn: confirmClientUserEmail,
    onSuccess: () => {
      addToast({
        type: 'success',
        message: 'Email confirmed — they can sign in with their existing password.',
      })
      invalidate()
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || 'Confirm failed.',
    }),
  })

  const disableMut = useMutation({
    mutationFn: disableClientUser,
    onSuccess: () => {
      addToast({ type: 'success', message: 'Account disabled.' })
      invalidate()
      setModal(null)
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Disable failed.' }),
  })

  const enableMut = useMutation({
    mutationFn: enableClientUser,
    onSuccess: () => {
      addToast({ type: 'success', message: 'Account re-enabled.' })
      invalidate()
      setModal(null)
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Enable failed.' }),
  })

  const deleteMut = useMutation({
    mutationFn: ({ userId, email }) => deleteClientUser(userId, email),
    onSuccess: () => {
      addToast({ type: 'success', message: 'Account deleted.' })
      invalidate()
      setModal(null)
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Delete failed.' }),
  })

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / perPage))

  return (
    <div className="space-y-4">
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Client Admin</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Manage portal logins for your clients. Each user is linked to one
            or more client accounts and can only see those clients' data.
          </p>
        </div>
        <button
          onClick={() => setModal({ mode: 'create' })}
          className="btn-primary text-sm flex items-center gap-1.5"
        >
          <UserPlus size={14} /> Create client account
        </button>
      </div>

      {/* Filter bar */}
      <div className="card px-4 py-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
            placeholder="Search by email, name, or attached client…"
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        {search && (
          <button onClick={() => setSearch('')} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
            <X size={12} /> Clear
          </button>
        )}
        <select
          value={perPage}
          onChange={e => { setPerPage(Number(e.target.value)); setPage(1) }}
          className="text-xs border border-slate-200 rounded px-2 py-1.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400"
        >
          {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n} per page</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : isError ? (
          <div className="p-6 text-sm text-red-600">
            Failed to load: {error?.response?.data?.detail || error?.message || 'unknown error'}
          </div>
        ) : (data?.users || []).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <UserPlus size={32} className="text-slate-300" />
            <p className="text-sm font-medium text-slate-600">No client accounts yet</p>
            <p className="text-xs text-slate-400">Use the "Create client account" button to invite the first one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs">
                <tr>
                  <SortHeader col="email"           label="Email"    sort={sort} onSort={toggleSort} />
                  <SortHeader col="full_name"       label="Name"     sort={sort} onSort={toggleSort} />
                  <th className="px-3 py-3 text-left font-semibold text-slate-500 uppercase tracking-wider">Linked clients</th>
                  <th className="px-3 py-3 text-left font-semibold text-slate-500 uppercase tracking-wider">Role</th>
                  <th className="px-3 py-3 text-left font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <SortHeader col="last_sign_in_at" label="Last sign-in" sort={sort} onSort={toggleSort} />
                  <SortHeader col="created_at"      label="Created"  sort={sort} onSort={toggleSort} />
                  <th className="px-3 py-3 text-right font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data.users || []).map((u, i) => (
                  <tr key={u.id} className={i % 2 === 1 ? 'bg-slate-50/40' : ''}>
                    <td className="px-3 py-2.5 text-slate-700">{u.email || '—'}</td>
                    <td className="px-3 py-2.5 text-slate-700">{u.full_name || <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2.5 text-slate-600">
                      {u.clients && u.clients.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {u.clients.map(c => (
                            <span key={c.id} className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                              {c.name}
                            </span>
                          ))}
                        </div>
                      ) : <span className="text-slate-300 text-xs">No clients</span>}
                    </td>
                    <td className="px-3 py-2.5">{roleBadge(u.client_role)}</td>
                    <td className="px-3 py-2.5">{statusBadge(u.status)}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">{fmtDateTime(u.last_sign_in_at)}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">{fmtDate(u.created_at)}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setModal({ mode: 'edit', user: u })}
                          className="p-1.5 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => setModal({ mode: 'reset', user: u })}
                          className="p-1.5 rounded text-slate-400 hover:text-amber-600 hover:bg-amber-50"
                          title="Reset password"
                        >
                          <KeyRound size={14} />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(
                              u.status === 'invited'
                                ? `Send a fresh invite email to ${u.email}?`
                                : `Send a password reset email to ${u.email}?`
                            )) {
                              resendMut.mutate(u.id)
                            }
                          }}
                          disabled={resendMut.isPending}
                          className="p-1.5 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={u.status === 'invited'
                            ? 'Resend invite email'
                            : 'Send password reset email'}
                        >
                          {resendMut.isPending && resendMut.variables === u.id
                            ? <Loader2 size={14} className="animate-spin" />
                            : <Send size={14} />}
                        </button>
                        {u.status === 'invited' && (
                          <button
                            onClick={() => {
                              if (window.confirm(
                                `Mark ${u.email} as confirmed?\n\n` +
                                `They'll be able to sign in immediately with their ` +
                                `existing password. No email is sent.`
                              )) {
                                confirmMut.mutate(u.id)
                              }
                            }}
                            disabled={confirmMut.isPending}
                            className="p-1.5 rounded text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Confirm email — unblocks 'Email not confirmed' at sign-in"
                          >
                            {confirmMut.isPending && confirmMut.variables === u.id
                              ? <Loader2 size={14} className="animate-spin" />
                              : <MailCheck size={14} />}
                          </button>
                        )}
                        {u.status === 'disabled' ? (
                          <button
                            onClick={() => setModal({ mode: 'enable', user: u })}
                            className="p-1.5 rounded text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"
                            title="Re-enable"
                          >
                            <ShieldCheck size={14} />
                          </button>
                        ) : (
                          <button
                            onClick={() => setModal({ mode: 'disable', user: u })}
                            className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
                            title="Disable (reversible)"
                          >
                            <ShieldOff size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => setModal({ mode: 'delete', user: u })}
                          className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
                          title="Delete (permanent)"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="px-4 py-3 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 bg-white">
              <p className="text-xs text-slate-500">
                {data.total === 0 ? 'No accounts' :
                  `Showing ${((page - 1) * perPage) + 1}–${Math.min(page * perPage, data.total)} of ${data.total.toLocaleString()}`}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-2 py-1 text-xs rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                >Previous</button>
                <span className="text-xs text-slate-500 px-2">Page {page} / {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-2 py-1 text-xs rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                >Next</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Client Activity — logins + page navigation for client portal users */}
      <ClientActivitySection
        users={data?.users || []}
        clientsList={clientsList}
      />

      {/* Modals */}
      {modal?.mode === 'create' && (
        <CreateEditModal
          mode="create"
          clientsList={clientsList}
          onClose={() => setModal(null)}
          onSubmit={(payload) => createMut.mutate(payload)}
          isPending={createMut.isPending}
        />
      )}
      {modal?.mode === 'edit' && (
        <CreateEditModal
          mode="edit"
          user={modal.user}
          clientsList={clientsList}
          onClose={() => setModal(null)}
          onSubmit={(payload) => updateMut.mutate({ userId: modal.user.id, payload })}
          isPending={updateMut.isPending}
        />
      )}
      {modal?.mode === 'reset' && (
        <ResetPasswordModal
          user={modal.user}
          onClose={() => setModal(null)}
          onSubmit={(payload) => resetMut.mutate({ userId: modal.user.id, payload })}
          isPending={resetMut.isPending}
        />
      )}
      {modal?.mode === 'disable' && (
        <ConfirmModal
          title="Disable account"
          tone="danger"
          icon={ShieldOff}
          message={
            <>Disable the account for <b>{modal.user.email}</b>?
            They won't be able to sign in until you re-enable them.
            All their linked clients and history are preserved.</>
          }
          confirmLabel="Disable account"
          onConfirm={() => disableMut.mutate(modal.user.id)}
          onCancel={() => setModal(null)}
          isPending={disableMut.isPending}
        />
      )}
      {modal?.mode === 'enable' && (
        <ConfirmModal
          title="Re-enable account"
          tone="success"
          icon={ShieldCheck}
          message={
            <>Re-enable the account for <b>{modal.user.email}</b>?
            They'll be able to sign in immediately.</>
          }
          confirmLabel="Re-enable account"
          onConfirm={() => enableMut.mutate(modal.user.id)}
          onCancel={() => setModal(null)}
          isPending={enableMut.isPending}
        />
      )}
      {modal?.mode === 'delete' && (
        <DeleteModal
          user={modal.user}
          onConfirm={(email) => deleteMut.mutate({ userId: modal.user.id, email })}
          onCancel={() => setModal(null)}
          isPending={deleteMut.isPending}
        />
      )}
    </div>
  )
}


// ─── Create / Edit modal ────────────────────────────────────────────────────


function CreateEditModal({ mode, user, clientsList, onClose, onSubmit, isPending }) {
  const isEdit = mode === 'edit'

  const [email, setEmail] = useState(user?.email || '')
  const [fullName, setFullName] = useState(user?.full_name || '')
  const [clientRole, setClientRole] = useState(user?.client_role || 'client_user')
  const [selectedIds, setSelectedIds] = useState(() => new Set((user?.clients || []).map(c => c.id)))
  const [passwordMode, setPasswordMode] = useState('invite')
  const [tempPassword, setTempPassword] = useState('')
  const [clientSearch, setClientSearch] = useState('')
  const [error, setError] = useState(null)

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase()
    if (!q) return clientsList
    return clientsList.filter(c => c.name.toLowerCase().includes(q))
  }, [clientsList, clientSearch])

  function toggleClient(id) {
    setSelectedIds(s => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    if (!isEdit) {
      if (!email.trim()) return setError('Email is required.')
      if (!/^\S+@\S+\.\S+$/.test(email.trim())) return setError('Email looks invalid.')
    }
    if (!fullName.trim()) return setError('Full name is required.')
    if (!isEdit && passwordMode === 'manual') {
      if (tempPassword.length < 8) return setError('Temporary password must be at least 8 characters.')
    }

    if (isEdit) {
      onSubmit({
        full_name:   fullName.trim(),
        client_role: clientRole,
        client_ids:  Array.from(selectedIds),
      })
    } else {
      onSubmit({
        email:         email.trim(),
        full_name:     fullName.trim(),
        client_role:   clientRole,
        client_ids:    Array.from(selectedIds),
        password_mode: passwordMode,
        ...(passwordMode === 'manual' ? { temp_password: tempPassword } : {}),
      })
    }
  }

  return (
    <ModalShell onClose={onClose} maxWidth="max-w-2xl">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
        <div>
          <h3 className="text-base font-semibold text-slate-800">{isEdit ? 'Edit client account' : 'Create client account'}</h3>
          {isEdit && (
            <p className="text-xs text-slate-400 mt-0.5">{user.email}</p>
          )}
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
      </div>

      <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Email{!isEdit && <span className="text-red-500">*</span>}</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={isEdit}
            required={!isEdit}
            placeholder="name@company.com"
            className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-500"
          />
          {isEdit && (
            <p className="text-xs text-slate-400 mt-1">Email is read-only. Contact Supabase support if you need to change it.</p>
          )}
        </div>

        {/* Full name */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Full name<span className="text-red-500">*</span></label>
          <input
            type="text"
            value={fullName}
            onChange={e => setFullName(e.target.value)}
            required
            placeholder="Charlie Middleton"
            className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Role */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
          <select
            value={clientRole}
            onChange={e => setClientRole(e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="client_user">Client User</option>
            <option value="client_admin">Client Admin</option>
          </select>
          <p className="text-xs text-slate-400 mt-1">
            Both roles see the same data today. Client Admin is reserved for future per-account admin actions.
          </p>
        </div>

        {/* Client linking */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Linked client accounts</label>
          <div className="border border-slate-300 rounded overflow-hidden">
            <div className="p-2 border-b border-slate-200 bg-slate-50">
              <div className="relative">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={clientSearch}
                  onChange={e => setClientSearch(e.target.value)}
                  placeholder="Filter clients…"
                  className="w-full pl-8 pr-3 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {filteredClients.length === 0 ? (
                <div className="p-3 text-xs text-slate-400 text-center">No matching clients.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {filteredClients.map(c => (
                    <li key={c.id}>
                      <label className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(c.id)}
                          onChange={() => toggleClient(c.id)}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-slate-700">{c.name}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="px-3 py-1.5 border-t border-slate-200 bg-slate-50 text-xs text-slate-500">
              {selectedIds.size === 0
                ? <span className="text-amber-600">No clients selected — this user won't see any data after sign-in.</span>
                : `${selectedIds.size} client${selectedIds.size === 1 ? '' : 's'} selected`}
            </div>
          </div>
        </div>

        {/* Password mode — only on create */}
        {!isEdit && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="password_mode"
                  value="invite"
                  checked={passwordMode === 'invite'}
                  onChange={() => setPasswordMode('invite')}
                  className="mt-0.5 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">
                  <span className="font-medium text-slate-700 flex items-center gap-1"><MailCheck size={13} /> Send invite email</span>
                  <span className="block text-xs text-slate-500">The user gets a magic-link email and sets their own password.</span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="password_mode"
                  value="manual"
                  checked={passwordMode === 'manual'}
                  onChange={() => setPasswordMode('manual')}
                  className="mt-0.5 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">
                  <span className="font-medium text-slate-700 flex items-center gap-1"><KeyRound size={13} /> Set a temporary password</span>
                  {/* Say plainly that nothing is sent. create_user() mails
                      nobody — only the invite branch does — and an account
                      created this way sits Active with no sign-in while
                      everyone waits for an email that was never coming. */}
                  <span className="block text-xs text-slate-500">
                    You pick a starter password and <span className="font-medium text-amber-700">pass it to them yourself —
                    no email is sent</span>. They&rsquo;re asked to change it on first sign-in.
                  </span>
                </span>
              </label>
              {passwordMode === 'manual' && (
                <input
                  type="password"
                  value={tempPassword}
                  onChange={e => setTempPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="ml-6 w-[calc(100%-1.5rem)] px-3 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50">
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {isPending && <Loader2 size={12} className="animate-spin" />}
            {isEdit ? 'Save changes' : passwordMode === 'invite' ? 'Send invite' : 'Create account'}
          </button>
        </div>
      </form>
    </ModalShell>
  )
}


// ─── Reset password modal ───────────────────────────────────────────────────


function ResetPasswordModal({ user, onClose, onSubmit, isPending }) {
  const [mode, setMode]                       = useState('email')
  const [newPassword, setNewPassword]         = useState('')
  const [confirmManual, setConfirmManual]     = useState(false)
  const [error, setError]                     = useState(null)

  function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    if (mode === 'manual') {
      if (newPassword.length < 8) return setError('Password must be at least 8 characters.')
      if (!confirmManual)        return setError('Please confirm by ticking the box.')
      onSubmit({ mode: 'manual', new_password: newPassword })
    } else {
      onSubmit({ mode: 'email' })
    }
  }

  return (
    <ModalShell onClose={onClose} maxWidth="max-w-lg">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
        <div>
          <h3 className="text-base font-semibold text-slate-800">Reset password</h3>
          <p className="text-xs text-slate-400 mt-0.5">{user.email}</p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
      </div>

      <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
        <div className="space-y-2">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="reset_mode"
              value="email"
              checked={mode === 'email'}
              onChange={() => setMode('email')}
              className="mt-0.5 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm">
              <span className="font-medium text-slate-700 flex items-center gap-1"><MailCheck size={13} /> Send password-reset email</span>
              <span className="block text-xs text-slate-500">The user gets a Supabase recovery link.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="reset_mode"
              value="manual"
              checked={mode === 'manual'}
              onChange={() => setMode('manual')}
              className="mt-0.5 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm">
              <span className="font-medium text-slate-700 flex items-center gap-1"><KeyRound size={13} /> Set a new password manually</span>
              <span className="block text-xs text-slate-500">You set a starter password. User is prompted to change it on next sign-in.</span>
            </span>
          </label>
        </div>

        {mode === 'manual' && (
          <div className="space-y-2 pl-6 border-l-2 border-amber-200">
            <input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="New password (8+ characters)"
              className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <label className="flex items-start gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={confirmManual}
                onChange={e => setConfirmManual(e.target.checked)}
                className="mt-0.5 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
              />
              <span>I confirm I want to set this password and have a way to communicate it to the user securely.</span>
            </label>
          </div>
        )}

        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">{error}</div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50">
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="px-3 py-1.5 text-sm rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {isPending && <Loader2 size={12} className="animate-spin" />}
            {mode === 'email' ? 'Send reset email' : 'Set password'}
          </button>
        </div>
      </form>
    </ModalShell>
  )
}


// ─── Confirm modal (disable / enable) ───────────────────────────────────────


function ConfirmModal({ title, message, confirmLabel, onConfirm, onCancel, isPending, tone = 'danger', icon: Icon }) {
  const toneCls = tone === 'danger'
    ? 'bg-red-600 hover:bg-red-700'
    : tone === 'success'
      ? 'bg-emerald-600 hover:bg-emerald-700'
      : 'bg-blue-600 hover:bg-blue-700'
  return (
    <ModalShell onClose={onCancel} maxWidth="max-w-md">
      <div className="px-6 py-5">
        <div className="flex items-center gap-2 mb-3">
          {Icon && <Icon size={20} className={tone === 'danger' ? 'text-red-500' : 'text-emerald-500'} />}
          <h3 className="text-base font-semibold text-slate-800">{title}</h3>
        </div>
        <p className="text-sm text-slate-600">{message}</p>
        <div className="flex items-center justify-end gap-2 pt-4 mt-4 border-t border-slate-100">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className={`px-3 py-1.5 text-sm rounded text-white disabled:opacity-50 inline-flex items-center gap-1.5 ${toneCls}`}
          >
            {isPending && <Loader2 size={12} className="animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </ModalShell>
  )
}


// ─── Delete modal (type-to-confirm) ─────────────────────────────────────────


function DeleteModal({ user, onConfirm, onCancel, isPending }) {
  const [typed, setTyped] = useState('')
  const expected = (user.email || '').trim().toLowerCase()
  const matches  = typed.trim().toLowerCase() === expected

  return (
    <ModalShell onClose={onCancel} maxWidth="max-w-lg">
      <div className="px-6 py-5">
        <div className="flex items-center gap-2 mb-3">
          <Trash2 size={20} className="text-red-500" />
          <h3 className="text-base font-semibold text-slate-800">Delete account permanently</h3>
        </div>

        <div className="text-sm text-slate-600 space-y-2">
          <p>
            This will permanently remove <b>{user.email}</b> and unlink them from{' '}
            {user.clients?.length ? (
              <>their <b>{user.clients.length}</b> linked client account{user.clients.length === 1 ? '' : 's'}</>
            ) : 'all client accounts'}. There is no undo.
          </p>
          <p className="text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded text-xs flex items-start gap-2">
            <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
            <span>If you only need to stop them logging in for now, use <b>Disable</b> instead — that's reversible.</span>
          </p>
        </div>

        <div className="mt-4 space-y-1">
          <label className="block text-xs font-medium text-slate-600">
            Type <span className="font-mono text-slate-800">{user.email}</span> to confirm
          </label>
          <input
            type="text"
            value={typed}
            onChange={e => setTyped(e.target.value)}
            placeholder={user.email}
            autoFocus
            className="w-full px-3 py-1.5 text-sm font-mono border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-4 mt-4 border-t border-slate-100">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50">
            Cancel
          </button>
          <button
            onClick={() => matches && onConfirm(user.email)}
            disabled={!matches || isPending}
            className="px-3 py-1.5 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          >
            {isPending && <Loader2 size={12} className="animate-spin" />}
            Delete forever
          </button>
        </div>
      </div>
    </ModalShell>
  )
}


// ─── Modal shell ────────────────────────────────────────────────────────────


function ModalShell({ children, onClose, maxWidth = 'max-w-md' }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto" onClick={onClose}>
      <div className="flex min-h-full items-start justify-center p-4 py-8">
        <div
          className={`bg-white rounded-2xl shadow-2xl w-full ${maxWidth}`}
          onClick={e => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </div>
  )
}


// ─── Client Activity section ────────────────────────────────────────────────
//
// Renders below the Client Admin table. Shows a paginated + filterable log of
// login and page-navigation events for client portal users. Filters:
// user, client, event type, date range, page size.
//
// Expected server response (GET /admin/client-activity):
//   { events: [ { id, occurred_at, user_id, user_email, user_full_name,
//                 client_id, client_name, event_type, path, ip, user_agent } ],
//     total, page, page_size, from, to }


// Default date-range window (30 days back). Defined once at module load so the
// section can seed its state without re-computing on every render.
const DEFAULT_ACTIVITY_DAYS = 30

function defaultActivityDates() {
  const to   = new Date()
  const from = new Date()
  from.setDate(from.getDate() - DEFAULT_ACTIVITY_DAYS)
  return { from: isoDate(from), to: isoDate(to) }
}

function eventBadge(type) {
  if (type === 'login') {
    return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">Login</span>
  }
  if (type === 'nav') {
    return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">Nav</span>
  }
  return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">{type || '—'}</span>
}

function ClientActivitySection({ users, clientsList }) {
  const seed = defaultActivityDates()

  const [userId,    setUserId]    = useState('')      // '' = All
  const [clientId,  setClientId]  = useState('')      // '' = All
  const [eventType, setEventType] = useState('all')   // all | login | nav
  const [dateFrom,  setDateFrom]  = useState(seed.from)
  const [dateTo,    setDateTo]    = useState(seed.to)
  const [page,      setPage]      = useState(1)
  const [perPage,   setPerPage]   = useState(25)

  // Reset to page 1 whenever a filter changes.
  function updateFilter(setter) {
    return (val) => { setter(val); setPage(1) }
  }

  function clearFilters() {
    const fresh = defaultActivityDates()
    setUserId('')
    setClientId('')
    setEventType('all')
    setDateFrom(fresh.from)
    setDateTo(fresh.to)
    setPerPage(25)
    setPage(1)
  }

  // Sorted user options — dropdown source is the same list the admin table
  // is showing above, so this reflects the current search context.
  const userOptions = useMemo(() => {
    return [...(users || [])]
      .filter(u => u.id && u.email)
      .sort((a, b) => (a.email || '').localeCompare(b.email || ''))
  }, [users])

  // Param names match the backend's GET /admin/client-activity signature:
  // `from` (Query alias), `to`, `page_size`. Sending `date_from`/`date_to`/
  // `per_page` silently gets ignored server-side and every request falls
  // back to the last-30-days default.
  const params = useMemo(() => ({
    user_id:    userId    || undefined,
    client_id:  clientId  || undefined,
    event_type: eventType === 'all' ? undefined : eventType,
    from:       dateFrom  || undefined,
    to:         dateTo    || undefined,
    page,
    page_size:  perPage,
  }), [userId, clientId, eventType, dateFrom, dateTo, page, perPage])

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['client-activity', params],
    queryFn:  () => getClientActivity(params),
    keepPreviousData: true,
  })

  const events     = data?.events || []
  const total      = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / perPage))

  const hasFilters = !!(userId || clientId || eventType !== 'all')

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex flex-wrap items-start justify-between gap-3 pt-2">
        <div className="flex items-start gap-2">
          <Activity size={18} className="text-slate-500 mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Client Activity</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Sign-ins and page navigation for client portal users. Useful for
              troubleshooting access issues and seeing what a client has looked at.
            </p>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="card px-4 py-3 flex flex-wrap items-center gap-3">
        {/* User dropdown */}
        <select
          value={userId}
          onChange={e => updateFilter(setUserId)(e.target.value)}
          className="text-xs border border-slate-300 rounded px-2 py-1.5 text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400 min-w-[180px]"
        >
          <option value="">All users</option>
          {userOptions.map(u => (
            <option key={u.id} value={u.id}>
              {u.email}{u.full_name ? ` — ${u.full_name}` : ''}
            </option>
          ))}
        </select>

        {/* Client dropdown */}
        <select
          value={clientId}
          onChange={e => updateFilter(setClientId)(e.target.value)}
          className="text-xs border border-slate-300 rounded px-2 py-1.5 text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400 min-w-[180px]"
        >
          <option value="">All clients</option>
          {clientsList.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        {/* Event type toggle */}
        <div className="inline-flex rounded border border-slate-300 overflow-hidden">
          {['all', 'login', 'nav'].map(t => (
            <button
              key={t}
              onClick={() => updateFilter(setEventType)(t)}
              className={`px-2.5 py-1.5 text-xs capitalize border-l first:border-l-0 border-slate-200 ${
                eventType === t
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              {t === 'all' ? 'All' : t === 'login' ? 'Login' : 'Nav'}
            </button>
          ))}
        </div>

        {/* Date range */}
        <div className="flex items-center gap-1 text-xs">
          <span className="text-slate-500">From</span>
          <input
            type="date"
            value={dateFrom}
            onChange={e => updateFilter(setDateFrom)(e.target.value)}
            className="border border-slate-300 rounded px-2 py-1 text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          <span className="text-slate-500">to</span>
          <input
            type="date"
            value={dateTo}
            onChange={e => updateFilter(setDateTo)(e.target.value)}
            className="border border-slate-300 rounded px-2 py-1 text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>

        {/* Page size */}
        <select
          value={perPage}
          onChange={e => { setPerPage(Number(e.target.value)); setPage(1) }}
          className="text-xs border border-slate-200 rounded px-2 py-1.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400 ml-auto"
        >
          {[25, 50, 100].map(n => <option key={n} value={n}>{n} per page</option>)}
        </select>

        {/* Clear filters */}
        {(hasFilters || dateFrom !== seed.from || dateTo !== seed.to || perPage !== 25) && (
          <button
            onClick={clearFilters}
            className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
          >
            <X size={12} /> Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : isError ? (
          <div className="p-6 text-sm text-red-600">
            Failed to load: {error?.response?.data?.detail || error?.message || 'unknown error'}
          </div>
        ) : events.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <Activity size={32} className="text-slate-300" />
            <p className="text-sm font-medium text-slate-600">No activity matches these filters</p>
            <p className="text-xs text-slate-400">Try widening the date range or clearing filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs">
                <tr>
                  <th className="px-3 py-3 text-left  font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">When</th>
                  <th className="px-3 py-3 text-left  font-semibold text-slate-500 uppercase tracking-wider">User</th>
                  <th className="px-3 py-3 text-left  font-semibold text-slate-500 uppercase tracking-wider">Client</th>
                  <th className="px-3 py-3 text-left  font-semibold text-slate-500 uppercase tracking-wider">Event</th>
                  <th className="px-3 py-3 text-left  font-semibold text-slate-500 uppercase tracking-wider">Path</th>
                  <th className="px-3 py-3 text-left  font-semibold text-slate-500 uppercase tracking-wider">IP</th>
                  <th className="px-3 py-3 text-left  font-semibold text-slate-500 uppercase tracking-wider">User agent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {events.map((e, i) => {
                  const ua = e.user_agent || ''
                  const uaShort = ua.length > 40 ? ua.slice(0, 40) + '…' : ua
                  return (
                    <tr key={e.id ?? i} className={i % 2 === 1 ? 'bg-slate-50/40' : ''}>
                      <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">
                        {fmtDateTimeShort(e.occurred_at)}
                      </td>
                      <td className="px-3 py-2.5 text-slate-700">
                        <div className="text-sm">{e.user_email || <span className="text-slate-400">—</span>}</div>
                        {e.user_full_name && <div className="text-xs text-slate-400">{e.user_full_name}</div>}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 text-sm">
                        {e.client_name || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5">{eventBadge(e.event_type)}</td>
                      <td className="px-3 py-2.5 text-xs font-mono text-slate-600 max-w-[240px] truncate" title={e.path || ''}>
                        {e.event_type === 'login' ? '' : (e.path || <span className="text-slate-300 font-sans">—</span>)}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-500 whitespace-nowrap">
                        {e.ip || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-500" title={ua}>
                        {uaShort || <span className="text-slate-300">—</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="px-4 py-3 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 bg-white">
              <p className="text-xs text-slate-500">
                {total === 0 ? 'No events' :
                  `Showing ${((page - 1) * perPage) + 1}–${Math.min(page * perPage, total)} of ${total.toLocaleString()}`}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-2 py-1 text-xs rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                >Previous</button>
                <span className="text-xs text-slate-500 px-2">Page {page} / {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-2 py-1 text-xs rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                >Next</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
